import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getConvertingFiles,
  getUploadedFilesSelector,
  getSnackMessage,
  files,
  getFileErrorType,
  app,
  getMultipleFileStatus,
  getFileRequestKeys,
  inspection,
  getIsReopenedSession,
  getInspectionId,
} from 'store';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import { getSourceFileUnavailable, getTargetFileUnavailable } from 'store/files/filesSelectors';
import { cancelUpload, fetchFile, finishFileCreation, startFileCreation } from 'store/requests';
import { useDropzone } from 'react-dropzone';
import { DocumentStates, DocumentTypes } from 'types';
import { FileErrors } from '@verify/shared-utils';
import GVErrorDialog from 'components/common/GVErrorDialog/GVErrorDialog';
import {
  startBatchFileCreation,
  finishBatchFileCreation,
  fetchMultipleFiles,
  pollFilesStatus,
  pollFileStatus,
} from 'store/request/files/actions';
import { FailedFile, PendingFile, PendingFileStatus } from 'store/request/files/types';
import { useQuery } from '@redux-requests/react';
import { stopPolling, abortRequests } from '@redux-requests/core';
import DragAndDropBackground from './DragAndDropBackground';
import UploadProgressBackground from './UploadProgressBackgound';
import { AxiosProgressEvent } from 'axios';
import fileActions from 'store/files/files';
import { computeSHA256 } from 'utils/computeChecksum';
import { getFileErrorMessage, supportedFileTypes, lowerCaseExtension } from './uploadHelpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { v4 as uuidv4 } from 'uuid';

interface PageBackgroundProps {
  document: DocumentTypes;
  title: string;
  dragging: boolean;
  setDragging: (value: React.SetStateAction<boolean>) => void;
  handleShowLibrary: (value?: boolean) => void;
  multipleLocations: boolean;
  setMultipleLocations: (value: boolean) => void;
}

const PageBackground = (props: PageBackgroundProps) => {
  const { dragging, setDragging, handleShowLibrary, multipleLocations, setMultipleLocations } = props;
  const [uploading, setUploading] = useState<number | false>(false);
  const [removeHeadersFooters, setRemoveHeadersFooters] = useState<boolean>(true);
  const [autoSelectLayersSeparations, setAutoSelectLayersSeparations] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileErrorType = useSelector(getFileErrorType);
  const convertingFiles = useSelector(getConvertingFiles);
  const filesState = useSelector(getUploadedFilesSelector);
  const errorMessage = useSelector(getSnackMessage);
  const multipleFileStatus = useSelector(getMultipleFileStatus)[props.document];
  const fileRequestKey = useSelector(getFileRequestKeys)[props.document];
  const isReopenedSession = useSelector(getIsReopenedSession);
  const inspectionId = useSelector(getInspectionId);
  const { isShared } = useCollaborators(inspectionId || undefined);
  const fileUnavailable = useSelector(
    props.document === DocumentTypes.source ? getSourceFileUnavailable : getTargetFileUnavailable,
  );
  const featureFlags = useFlags();
  const pollFileStatusQuery = pollFileStatus.toString() + fileRequestKey;
  const dispatch = useDispatch();
  const { data: multipleFilesData } = useQuery({ type: fetchMultipleFiles });
  const { fileId } = filesState[props.document];
  const [uploadText, overrideUploadText] = useState<string | null>(null);
  const [uploadVariant, overrideUploadVariant] = useState<'determinate' | 'indeterminate'>('determinate');
  const KNOWN_BAD_CHECKSUM_ERRORS = featureFlags.knownBadChecksumErrors || {}; // format - { 'HASH_ID': 'error message' }

  const handleCancelUpload = () => {
    overrideUploadVariant('determinate');
    overrideUploadText(null);
    setUploading(false);
    setUploadProgress(0);
    dispatch(cancelUpload());
    dispatch(stopPolling([pollFilesStatus.toString()]));
    dispatch(stopPolling([pollFileStatusQuery]));
    dispatch(files.actions.setMultipleFileStatus({ documentType: props.document, status: false }));
    dispatch(
      inspection.actions.setLoadingState({ documentType: props.document, documentState: DocumentStates.INITIAL }),
    );
    dispatch(
      abortRequests([
        finishFileCreation.toString(),
        startFileCreation.toString(),
        startBatchFileCreation.toString(),
        finishBatchFileCreation.toString(),
        fetchFile.toString(),
        fetchMultipleFiles.toString(),
        pollFilesStatus.toString(),
        pollFileStatusQuery,
      ]),
    );
    dispatch(files.actions.removeFile({ documentType: props.document }));
    dispatch(files.actions.setFileCreationCancelled(true));
  };

  useEffect(() => {
    if (convertingFiles[props.document] && !fileErrorType) {
      dispatch(pollFileStatus(fileId, props.document));
    }
  }, [dispatch, convertingFiles, props.document, fileId, fileErrorType]);

  useEffect(() => {
    if (errorMessage?.open || fileErrorType) {
      setUploading(false);
      dispatch(files.actions.removeFile({ documentType: props.document }));
    }
  }, [dispatch, errorMessage, props.document, setUploading, fileErrorType]);

  useEffect(() => {
    if (!multipleFilesData && !multipleFileStatus) {
      setUploading(false);
    }
  }, [multipleFilesData, multipleFileStatus]);

  const updateUploadProgress = (progressEvent: AxiosProgressEvent) => {
    overrideUploadVariant('determinate');
    overrideUploadText(null);
    if (progressEvent.total) {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      if (progress >= 100) {
        setTimeout(() => {
          overrideUploadText('Pre-processing Your File(s)...');
          overrideUploadVariant('indeterminate');
        }, 700);
      }
      setUploadProgress(progress >= 100 ? 100 : progress);
    }
  };

  const dialogProps = {
    title: 'Error loading File',
    subTitle: getFileErrorMessage(fileErrorType),
    open: fileErrorType !== undefined,
    buttonText: 'Dismiss',
    handleCallToActionClick: () => {
      dispatch(files.actions.setIsFileErrorOpen(undefined));
    },
    handleCloseModal: () => {
      dispatch(files.actions.setIsFileErrorOpen(undefined));
    },
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: supportedFileTypes,
    onDrop: async (accepted, rejected) => {
      const fileDropCount = accepted.length + rejected.length; // how many files the user dropped

      if (fileDropCount > 20) {
        dispatch(
          app.actions.setSnackMessage({
            message: 'There is a maximum of 20 files that can be uploaded at one time',
            type: 'error',
          }),
        );
        return;
      }

      if (accepted.length === 0) {
        dispatch(
          app.actions.setSnackMessage({
            message: 'Unsupported file type.',
            type: 'error',
          }),
        );
        return;
      }

      const renamedAcceptedFiles = accepted.map(
        (file) => new File([file], lowerCaseExtension(file.name), { type: file.type }),
      );

      setUploading(renamedAcceptedFiles.length);
      setDragging(false);
      dispatch(fileActions.actions.setFileCreationCancelled(false));

      // Clear any stale cancelled lists from previous upload sessions
      dispatch(fileActions.actions.clearCancelledFileIds());
      dispatch(fileActions.actions.clearCancelledFilenames());
      dispatch(fileActions.actions.clearCancelledBatchKeys());
      dispatch(fileActions.actions.clearFileToBatchMap());

      // Use batch flow if multiple files OR if "multiple locations" checkbox is checked
      const useBatchFlow = fileDropCount > 1 || multipleLocations;

      if (fileDropCount === 1 && !multipleLocations) {
        // single file upload (direct open, no reorder modal)
        const file = renamedAcceptedFiles[0];
        const checksum = await computeSHA256(file as File);
        if (checksum in KNOWN_BAD_CHECKSUM_ERRORS) {
          dispatch(
            app.actions.setSnackMessage({
              message: KNOWN_BAD_CHECKSUM_ERRORS[checksum],
              type: 'error',
            }),
          );
          return;
        }

        const fileData = new FormData();

        fileData.append('file', file);
        fileData.append('checksum', checksum);

        dispatch(
          startFileCreation(
            fileData,
            props.document,
            updateUploadProgress,
            removeHeadersFooters,
            autoSelectLayersSeparations,
          ),
        );
      } else if (useBatchFlow) {
        // multiple file upload OR single file with "multiple locations" checked
        // This will show the ReorderFiles modal

        // Create pending files immediately to show the modal right away
        const pendingFiles: PendingFile[] = renamedAcceptedFiles.map((file) => ({
          id: `pending-${uuidv4()}`, // Temporary ID until we get real ID from API
          originalFilename: file.name,
          status: PendingFileStatus.uploading,
          progress: 0,
        }));

        // Show the modal immediately with pending files
        dispatch(
          files.actions.setPendingReorderData({
            pendingFiles,
            documentType: props.document,
            removeHeadersFooters,
          }),
        );

        const filesData = new FormData();

        await Promise.all(
          renamedAcceptedFiles.map(async (file) => {
            const checksum = await computeSHA256(file as File);
            return { file, checksum };
          }),
        ).then((results) => {
          results.forEach(({ file, checksum }) => {
            filesData.append('files', file);
            filesData.append('checksums', checksum);
          });
        });

        const rejectedFiles: FailedFile[] = rejected.map((rejectedFile) => ({
          id: `rejected-${uuidv4()}`,
          filename: lowerCaseExtension(rejectedFile.file.name),
          error: FileErrors.incompatibleFileType,
        }));

        // Create mapping of temp IDs to filenames for ID replacement
        const pendingMapping = pendingFiles.map((p) => ({
          tempId: p.id,
          filename: p.originalFilename,
        }));

        dispatch(
          startBatchFileCreation(
            filesData,
            rejectedFiles,
            props.document,
            updateUploadProgress,
            removeHeadersFooters,
            autoSelectLayersSeparations,
            pendingMapping,
          ),
        );
      }
    },
    onDragEnter: () => {
      setDragging(true);
    },
    onDragLeave: () => {
      setDragging(false);
    },
  });

  const handleOpenedFromHtml = (isGvault: boolean) => {
    setUploadProgress(100);
    overrideUploadText(isGvault ? 'Opening from Gvault' : 'Opening from URL...');
    overrideUploadVariant('indeterminate');
  };

  const showRetentionMessage = (fileUnavailable || !fileId) && (isReopenedSession || isShared);
  return (
    <>
      <GVErrorDialog {...dialogProps} />
      {uploading || convertingFiles[props.document] ? (
        <UploadProgressBackground
          document={props.document}
          uploadProgress={uploadProgress}
          handleCancelUpload={handleCancelUpload}
          convertingFiles={convertingFiles}
          uploadCount={uploading}
          uploadText={uploadText}
          uploadVariant={uploadVariant}
        />
      ) : (
        <DragAndDropBackground
          document={props.document}
          title={props.title}
          handleShowLibrary={handleShowLibrary}
          setRemoveHeadersFooters={setRemoveHeadersFooters}
          removeHeadersFooters={removeHeadersFooters}
          setAutoSelectLayersSeparations={setAutoSelectLayersSeparations}
          autoSelectLayersSeparations={autoSelectLayersSeparations}
          multipleLocations={multipleLocations}
          setMultipleLocations={setMultipleLocations}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          dragging={dragging}
          onOpenedFromHtml={handleOpenedFromHtml}
          uploadDisabled={isShared && !fileId}
          showRetentionMessage={showRetentionMessage}
        />
      )}
    </>
  );
};

export default PageBackground;
